const UNSAFE_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly detail?: string,
    public readonly errors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function getCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1];
}

/**
 * Typed fetch wrapper. Every path is relative because the SPA is always served from the same
 * origin as the API — in development through the Vite proxy, in production from wwwroot.
 */
export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers = new Headers(options.headers);

  if (UNSAFE_METHODS.includes(method)) {
    // Double-submit CSRF: the server sets a readable XSRF-TOKEN cookie and requires it echoed back.
    const token = getCookie('XSRF-TOKEN');
    if (token) {
      headers.set('X-XSRF-TOKEN', decodeURIComponent(token));
    }
    if (options.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
  }

  const response = await fetch(path, { ...options, method, headers, credentials: 'include' });

  if (response.status === 204) {
    return undefined as T;
  }

  const isJson = response.headers.get('Content-Type')?.includes('application/json');
  const payload = isJson ? await response.json() : undefined;

  if (!response.ok) {
    handleSessionExpiry(response.status, path);

    throw new ApiError(
      response.status,
      payload?.title ?? response.statusText,
      payload?.detail,
      payload?.errors,
    );
  }

  return payload as T;
}

let redirectingToLogin = false;

/**
 * A 401 on a normal call means the cookie expired mid-session. Send the user to sign in rather
 * than leaving them on a page that silently fails, but never for the auth endpoints themselves —
 * a failed sign-in is an expected answer, not an expired session.
 */
function handleSessionExpiry(status: number, path: string) {
  if (status !== 401 || redirectingToLogin) {
    return;
  }

  if (path.startsWith('/api/auth/') || window.location.pathname === '/login') {
    return;
  }

  redirectingToLogin = true;
  const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.assign(`/login?returnUrl=${returnUrl}`);
}
